// components/DistrictEnrollmentTable.jsx
import React, { useState, useMemo, useCallback } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, ChevronRight } from 'lucide-react';

/**
 * Normalize district name for matching
 */
function normalizeDistrictName(name) {
  return name
    .toLowerCase()
    .replace(/\s*(school district|sd|district|schools?)\s*/gi, '')
    .replace(/[^a-z0-9]/g, '')
    .trim();
}

/**
 * Generate contextual notes for a district based on its data
 */
function getDistrictNotes(school, summary, maxPathways) {
  const notes = [];

  // Check for broadest footprint
  if (school.cipCount === maxPathways && school.cipCount > 3) {
    notes.push('Broadest footprint');
  }

  // Check for single pathway
  if (school.cipCount === 1) {
    notes.push('Single pathway');
  }

  // Check for zero enrollment
  if (school.totalEnrollment === 0) {
    notes.push('No enrollment');
  }

  // Check for focused program (2-3 pathways with good enrollment)
  if (school.cipCount >= 2 && school.cipCount <= 3 && school.totalEnrollment >= 100) {
    notes.push('Focused');
  }

  // Wide mix (many pathways, moderate total)
  if (school.cipCount >= 5 && school.totalEnrollment > 50 && school.totalEnrollment < summary.maxEnrollment * 0.3) {
    notes.push('Wide mix');
  }

  return notes;
}

/**
 * District Enrollment Table Component
 * Clean, readable overview of what each district offers
 * Designed for inventory, CLNA narratives, and grant documentation
 */
export default function DistrictEnrollmentTable({
  schoolData,
  summary,
  cipTotals,
  districtName,
  filterSession,
  regionalGroups = {}
}) {
  const [sortColumn, setSortColumn] = useState('enrollment');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedSchools, setExpandedSchools] = useState(new Set());
  const [isTotalExpanded, setIsTotalExpanded] = useState(false);

  // Calculate max pathways for notes
  const maxPathways = useMemo(() => {
    return Math.max(...schoolData.map(s => s.cipCount), 0);
  }, [schoolData]);

  // Build normalized lookup map for regions
  const regionLookup = useMemo(() => {
    const lookup = new Map();
    Object.entries(regionalGroups).forEach(([groupName, schools]) => {
      schools.forEach(school => {
        lookup.set(normalizeDistrictName(school), groupName);
      });
    });
    return lookup;
  }, [regionalGroups]);

  // Get district's regional group
  const getRegion = useCallback((schoolName) => {
    const normalized = normalizeDistrictName(schoolName);
    if (regionLookup.has(normalized)) {
      return regionLookup.get(normalized);
    }
    for (const [normalizedSchool, groupName] of regionLookup.entries()) {
      if (normalized.startsWith(normalizedSchool) || normalizedSchool.startsWith(normalized)) {
        return groupName;
      }
    }
    return "Other";
  }, [regionLookup]);

  // Group and sort data by region
  const groupedData = useMemo(() => {
    // Sort function
    const sortFn = (a, b) => {
      let aVal, bVal;

      switch (sortColumn) {
        case 'name':
          aVal = a.schoolName.toLowerCase();
          bVal = b.schoolName.toLowerCase();
          return sortDirection === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        case 'enrollment':
          aVal = a.totalEnrollment;
          bVal = b.totalEnrollment;
          break;
        case 'cipCount':
          aVal = a.cipCount;
          bVal = b.cipCount;
          break;
        default:
          aVal = a.totalEnrollment;
          bVal = b.totalEnrollment;
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return 0;
    };

    // If no regional groups, return flat sorted list
    if (Object.keys(regionalGroups).length === 0) {
      return [{ name: null, districts: [...schoolData].sort(sortFn) }];
    }

    // Group districts by region
    const regionMap = {};
    Object.keys(regionalGroups).forEach(region => {
      regionMap[region] = [];
    });
    regionMap["Other"] = [];

    schoolData.forEach(school => {
      const region = getRegion(school.schoolName);
      if (regionMap[region]) {
        regionMap[region].push(school);
      } else {
        regionMap["Other"].push(school);
      }
    });

    // Build ordered region list with sorted districts
    const regions = Object.keys(regionalGroups).map(name => ({
      name,
      districts: regionMap[name].sort(sortFn),
      totalEnrollment: regionMap[name].reduce((sum, d) => sum + d.totalEnrollment, 0),
      districtCount: regionMap[name].length
    }));

    // Add "Other" if it has districts
    if (regionMap["Other"].length > 0) {
      regions.push({
        name: "Other",
        districts: regionMap["Other"].sort(sortFn),
        totalEnrollment: regionMap["Other"].reduce((sum, d) => sum + d.totalEnrollment, 0),
        districtCount: regionMap["Other"].length
      });
    }

    return regions;
  }, [schoolData, sortColumn, sortDirection, regionalGroups, getRegion]);

  // Handle column header click
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection(column === 'name' ? 'asc' : 'desc');
    }
  };

  // Toggle school expansion
  const toggleSchool = useCallback((schoolName) => {
    setExpandedSchools(prev => {
      const next = new Set(prev);
      if (next.has(schoolName)) {
        next.delete(schoolName);
      } else {
        next.add(schoolName);
      }
      return next;
    });
  }, []);

  // Sort icon component
  const SortIcon = ({ column }) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="w-3 h-3 text-zinc-400" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="w-3 h-3 text-zinc-600" />
    ) : (
      <ArrowDown className="w-3 h-3 text-zinc-600" />
    );
  };

  
  if (!schoolData.length) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-zinc-200 p-6">
        <div className="text-center text-zinc-500 py-8">
          No enrollment data available for the selected filter
        </div>
      </div>
    );
  }

  // Calculate districts with enrollment in the current filter
  const districtsWithEnrollment = schoolData.filter(s => s.totalEnrollment > 0).length;
  const districtsWithoutEnrollment = schoolData.filter(s => s.totalEnrollment === 0);
  const totalRegionalDistricts = Object.keys(regionalGroups).length > 0
    ? Object.values(regionalGroups).flat().length
    : null;

  return (
    <div className="max-w-3xl">
      {/* Summary Line - above the table */}
      <p className="text-sm text-zinc-600 mb-3" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {totalRegionalDistricts ? (
          <>
            <span className="font-semibold text-zinc-800">{districtsWithEnrollment}</span>
            {' '}of{' '}
            <span className="font-semibold text-zinc-800">{totalRegionalDistricts}</span>
            {' '}districts have{' '}
          </>
        ) : (
          <>
            <span className="font-semibold text-zinc-800">{districtsWithEnrollment}</span>
            {' '}districts with{' '}
          </>
        )}
        <span className="font-semibold text-indigo-600">{summary.totalEnrollment.toLocaleString()}</span>
        {' '}enrollments across{' '}
        <span className="font-semibold text-zinc-800">{summary.totalCips}</span>
        {' '}pathways
      </p>

      <div className="bg-white rounded-lg shadow-sm border border-zinc-200 overflow-hidden">
        {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {/* Column Headers */}
          <thead>
            <tr className="border-b border-zinc-200 bg-white">
              <th
                className="text-left py-3 px-4 text-xs font-semibold text-zinc-600 uppercase tracking-wide cursor-pointer hover:bg-zinc-50"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-2">
                  District
                  <SortIcon column="name" />
                </div>
              </th>
              <th
                className="text-right py-3 px-4 text-xs font-semibold text-zinc-600 uppercase tracking-wide cursor-pointer hover:bg-zinc-50 w-[120px]"
                onClick={() => handleSort('enrollment')}
              >
                <div className="flex items-center justify-end gap-2">
                  Enrolled
                  <SortIcon column="enrollment" />
                </div>
              </th>
              <th
                className="text-center py-3 px-4 text-xs font-semibold text-zinc-600 uppercase tracking-wide cursor-pointer hover:bg-zinc-50 w-[100px]"
                onClick={() => handleSort('cipCount')}
              >
                <div className="flex items-center justify-center gap-2">
                  Pathways
                  <SortIcon column="cipCount" />
                </div>
              </th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-zinc-600 uppercase tracking-wide w-[200px]">
                Notes
              </th>
            </tr>
          </thead>

          <tbody>
            {/* Total Row */}
            <tr
              className="border-b border-zinc-600 cursor-pointer hover:bg-zinc-50 transition-colors"
              onClick={() => setIsTotalExpanded(!isTotalExpanded)}
            >
              <td className="py-3 px-4 text-sm font-semibold text-zinc-800">
                <div className="flex items-center gap-2">
                  {isTotalExpanded ? (
                    <ChevronDown className="w-4 h-4 text-zinc-600" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-zinc-400" />
                  )}
                  <span>{districtName || 'Total'}</span>
                </div>
              </td>
              <td className="py-3 px-4 text-sm font-semibold text-zinc-800 text-right">
                {summary.totalEnrollment.toLocaleString()}
              </td>
              <td className="py-3 px-4 text-sm font-semibold text-zinc-800 text-center">
                {summary.totalCips}
              </td>
              <td className="py-3 px-4 text-sm text-zinc-500">
                Regional total
              </td>
            </tr>

            {/* Expanded Total CIP Breakdown */}
            {isTotalExpanded && cipTotals.length > 0 && (
              <tr className="border-b border-zinc-200">
                <td colSpan={4} className="p-0">
                  <div className="ml-8 border-l-2 border-zinc-300 bg-zinc-50/50 py-2">
                    <table className="w-full">
                      <tbody>
                        {cipTotals.map((cip) => (
                          <tr key={cip.cipcode} className="hover:bg-zinc-100/50">
                            <td className="py-1.5 px-4 text-xs text-zinc-700">
                              {cip.cipcodeName}
                            </td>
                            <td className="py-1.5 px-4 text-xs font-medium text-zinc-600 text-right w-[120px]">
                              {cip.enrollment.toLocaleString()}
                            </td>
                            <td className="w-[100px]"></td>
                            <td className="w-[200px]"></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </td>
              </tr>
            )}

            {/* Region Sections */}
            {groupedData.map(region => (
              <React.Fragment key={region.name || 'all'}>
                {/* Region Header */}
                {region.name && (
                  <tr className="border-b border-zinc-600">
                    <td colSpan={4} className="py-3 px-4 bg-white">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-zinc-800">{region.name}</span>
                          <span className="text-xs text-zinc-400 font-normal">
                            {region.districtCount} districts
                          </span>
                        </div>
                        <span className="text-sm text-zinc-600" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {region.totalEnrollment.toLocaleString()} enrolled
                        </span>
                      </div>
                    </td>
                  </tr>
                )}

                {/* District Rows */}
                {region.districts.map((school, idx) => {
                  const isExpanded = expandedSchools.has(school.schoolName);
                  const notes = getDistrictNotes(school, summary, maxPathways);
                  const isZero = school.totalEnrollment === 0;

                  return (
                    <React.Fragment key={school.schoolName}>
                      {/* District Row */}
                      <tr
                        className={`group cursor-pointer transition-colors border-b border-zinc-100 ${
                          isZero ? 'bg-zinc-50/50 text-zinc-400' : idx % 2 === 0 ? 'bg-white hover:bg-zinc-50' : 'bg-zinc-50/30 hover:bg-zinc-100'
                        }`}
                        onClick={() => school.cipCount > 0 && toggleSchool(school.schoolName)}
                      >
                        <td className="py-3 px-4 text-sm text-zinc-900">
                          <div className="flex items-center gap-2">
                            {school.cipCount > 0 ? (
                              isExpanded ? (
                                <ChevronDown className="w-4 h-4 text-zinc-600 flex-shrink-0" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-zinc-400 group-hover:text-zinc-600 flex-shrink-0" />
                              )
                            ) : (
                              <div className="w-4 h-4 flex-shrink-0" />
                            )}
                            <span className={isZero ? 'text-zinc-400' : ''}>
                              {school.schoolName}
                            </span>
                          </div>
                        </td>
                        <td className={`py-3 px-4 text-sm text-right font-medium ${isZero ? 'text-zinc-400' : 'text-zinc-800'}`}>
                          {school.totalEnrollment.toLocaleString()}
                        </td>
                        <td className={`py-3 px-4 text-sm text-center ${isZero ? 'text-zinc-400' : 'text-zinc-600'}`}>
                          {school.cipCount}
                        </td>
                        <td className="py-3 px-4 text-xs text-zinc-500">
                          {notes.join(' · ')}
                        </td>
                      </tr>

                      {/* Expanded CIP Breakdown */}
                      {isExpanded && school.cips.length > 0 && (
                        <tr className="border-b border-zinc-200">
                          <td colSpan={4} className="p-0">
                            <div className="ml-8 border-l-2 border-zinc-300 bg-zinc-50/50 py-2">
                              <table className="w-full">
                                <tbody>
                                  {school.cips.map((cip) => (
                                    <tr key={cip.cipcode} className="hover:bg-zinc-100/50">
                                      <td className="py-1.5 px-4 text-xs text-zinc-700">
                                        {cip.cipcodeName}
                                      </td>
                                      <td className="py-1.5 px-4 text-xs font-medium text-zinc-600 text-right w-[120px]">
                                        {cip.enrollment.toLocaleString()}
                                      </td>
                                      <td className="w-[100px]"></td>
                                      <td className="w-[200px]"></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                        )}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Opportunity Callout */}
      {districtsWithoutEnrollment.length > 0 && (
        <div className="bg-amber-50 border-t border-amber-200 px-4 py-3">
          <div className="flex items-start gap-2 text-sm">
            <svg className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <span className="font-semibold text-amber-800">
                Opportunity ({districtsWithoutEnrollment.length} of {totalRegionalDistricts || schoolData.length} districts):
              </span>{' '}
              <span className="text-amber-700">
                {districtsWithoutEnrollment.map(d => d.schoolName).join(', ')} {districtsWithoutEnrollment.length === 1 ? 'has' : 'have'} no enrollments in this area.
              </span>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
